// Deterministic, data-driven world events. Base events always occur on the
// same day unless the player has set one of their preventedByFlags, or unless
// the event has conditions that aren't all met on that day.

import type { GameEvent, LoopState } from "../types"
import { events } from "./content"

export function getEventsForDay(day: number): GameEvent[] {
  return events.filter((event) => event.day === day)
}

export function isEventPrevented(event: GameEvent, loop: LoopState): boolean {
  return event.preventedByFlags.some((flag) => loop.worldFlags.includes(flag))
}

/** An event's `conditions` are world flags that must all be set for it to trigger at all (unlike preventedByFlags, which blocks it). */
export function areConditionsMet(event: GameEvent, loop: LoopState): boolean {
  return event.conditions.every((flag) => loop.worldFlags.includes(flag))
}

export function hasWorldFlag(loop: LoopState, flag: string): boolean {
  return loop.worldFlags.includes(flag)
}

/** Records a player decision (e.g. evacuating a village) as a world flag that can prevent or change future events. */
export function addWorldFlag(loop: LoopState, flag: string): LoopState {
  if (hasWorldFlag(loop, flag)) return loop
  return { ...loop, worldFlags: [...loop.worldFlags, flag] }
}

/**
 * Lifts a world flag. Flags are almost always one-way (a thing happened and
 * stays happened), but a few describe a reversible *state* rather than an
 * event — a stronghold's control, above all — and retaking it has to be able
 * to undo the "controlled_by_enemy" mark the fall set.
 */
export function removeWorldFlag(loop: LoopState, flag: string): LoopState {
  if (!hasWorldFlag(loop, flag)) return loop
  return { ...loop, worldFlags: loop.worldFlags.filter((f) => f !== flag) }
}

/**
 * Resolves events for a single day, marking triggered events as completed.
 * Effects are recorded as opaque ids; interpreting them (e.g. starting a
 * battle) is left to higher-level systems added in later phases.
 */
export function resolveEventsForDay(loop: LoopState, day: number): LoopState {
  const dayEvents = getEventsForDay(day)
  return dayEvents.reduce((current, event) => {
    if (current.completedEvents.includes(event.id)) return current
    if (isEventPrevented(event, current)) return current
    if (!areConditionsMet(event, current)) return current
    return {
      ...current,
      completedEvents: [...current.completedEvents, event.id],
    }
  }, loop)
}

export function resolveEventsForRange(
  loop: LoopState,
  fromDay: number,
  toDay: number,
): LoopState {
  let current = loop
  for (let day = fromDay; day <= toDay; day++) {
    current = resolveEventsForDay(current, day)
  }
  return current
}
